// Consolidated Supabase types and client
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = "https://colahfwujomsjetiwctq.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNvbGFoZnd1am9tc2pldGl3Y3RxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU2OTE5MjEsImV4cCI6MjA3MTI2NzkyMX0.y6KA-6E4vJPab0nSjrGz5n7dwHQy6S_0Nux55gSAZL8";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  }
});

export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export type Database = {
  __InternalSupabase: {
    PostgrestVersion: "13.0.4"
  }
  public: {
    Tables: {
      activities: {
        Row: {
          activity_type: string
          created_at: string
          description: string
          id: string
          reference_id: string | null
          status: string
          user_id: string
        }
        Insert: {
          activity_type: string
          created_at?: string
          description: string
          id?: string
          reference_id?: string | null
          status: string
          user_id: string
        }
        Update: {
          activity_type?: string
          created_at?: string
          description?: string
          id?: string
          reference_id?: string | null
          status?: string
          user_id?: string
        }
        Relationships: []
      }
      appointments: {
        Row: {
          appointment_date: string
          appointment_time: string
          appointment_type: string
          created_at: string
          duration_minutes: number | null
          id: string
          location: string | null
          notes: string | null
          provider_name: string | null
          reminder_sent: boolean | null
          status: string
          updated_at: string
          user_id: string
        }
        Insert: {
          appointment_date: string
          appointment_time: string
          appointment_type: string
          created_at?: string
          duration_minutes?: number | null
          id?: string
          location?: string | null
          notes?: string | null
          provider_name?: string | null
          reminder_sent?: boolean | null
          status?: string
          updated_at?: string
          user_id: string
        }
        Update: {
          appointment_date?: string
          appointment_time?: string
          appointment_type?: string
          created_at?: string
          duration_minutes?: number | null
          id?: string
          location?: string | null
          notes?: string | null
          provider_name?: string | null
          reminder_sent?: boolean | null
          status?: string
          updated_at?: string
          user_id?: string
        }
        Relationships: []
      }
      categories: {
        Row: {
          created_at: string
          description: string | null
          id: number
          name: string
        }
        Insert: {
          created_at?: string
          description?: string | null
          id?: number
          name: string
        }
        Update: {
          created_at?: string
          description?: string | null
          id?: number
          name?: string
        }
        Relationships: []
      }
      health_metrics: {
        Row: {
          created_at: string
          description: string | null
          id: string
          metric_type: string
          metric_unit: string | null
          metric_value: string
          recorded_at: string
          status: string
          trend: string
          user_id: string
        }
        Insert: {
          created_at?: string
          description?: string | null
          id?: string
          metric_type: string
          metric_unit?: string | null
          metric_value: string
          recorded_at?: string
          status: string
          trend?: string
          user_id: string
        }
        Update: {
          created_at?: string
          description?: string | null
          id?: string
          metric_type?: string
          metric_unit?: string | null
          metric_value?: string
          recorded_at?: string
          status?: string
          trend?: string
          user_id?: string
        }
        Relationships: []
      }
      hormone_cycles: {
        Row: {
          created_at: string
          cycle_length: number | null
          cycle_start_date: string
          energy_rating: number | null
          flow_intensity: string | null
          id: string
          is_pregnancy_tracked: boolean | null
          mood_rating: number | null
          notes: string | null
          ovulation_date: string | null
          period_end_date: string | null
          period_start_date: string | null
          pregnancy_test_result: boolean | null
          symptoms: string[] | null
          updated_at: string
          user_id: string
        }
        Insert: {
          created_at?: string
          cycle_length?: number | null
          cycle_start_date: string
          energy_rating?: number | null
          flow_intensity?: string | null
          id?: string
          is_pregnancy_tracked?: boolean | null
          mood_rating?: number | null
          notes?: string | null
          ovulation_date?: string | null
          period_end_date?: string | null
          period_start_date?: string | null
          pregnancy_test_result?: boolean | null
          symptoms?: string[] | null
          updated_at?: string
          user_id: string
        }
        Update: {
          created_at?: string
          cycle_length?: number | null
          cycle_start_date?: string
          energy_rating?: number | null
          flow_intensity?: string | null
          id?: string
          is_pregnancy_tracked?: boolean | null
          mood_rating?: number | null
          notes?: string | null
          ovulation_date?: string | null
          period_end_date?: string | null
          period_start_date?: string | null
          pregnancy_test_result?: boolean | null
          symptoms?: string[] | null
          updated_at?: string
          user_id?: string
        }
        Relationships: []
      }
      media_articles: {
        Row: {
          created_at: string
          featured: boolean
          id: number
          link: string
          name: string
          quotes: string | null
          tagline: string | null
          updated_at: string
        }
        Insert: {
          created_at?: string
          featured?: boolean
          id?: number
          link: string
          name: string
          quotes?: string | null
          tagline?: string | null
          updated_at?: string
        }
        Update: {
          created_at?: string
          featured?: boolean
          id?: number
          link?: string
          name?: string
          quotes?: string | null
          tagline?: string | null
          updated_at?: string
        }
        Relationships: []
      }
      medical_files: {
        Row: {
          created_at: string
          description: string | null
          file_category: string | null
          file_name: string
          file_path: string
          file_size: number
          file_type: string
          id: string
          upload_date: string
          user_id: string
        }
        Insert: {
          created_at?: string
          description?: string | null
          file_category?: string | null
          file_name: string
          file_path: string
          file_size: number
          file_type: string
          id?: string
          upload_date?: string
          user_id: string
        }
        Update: {
          created_at?: string
          description?: string | null
          file_category?: string | null
          file_name?: string
          file_path?: string
          file_size?: number
          file_type?: string
          id?: string
          upload_date?: string
          user_id?: string
        }
        Relationships: []
      }
      medication_logs: {
        Row: {
          created_at: string
          delay_minutes: number | null
          id: string
          medication_id: string
          notes: string | null
          scheduled_time: string
          status: string
          taken_time: string | null
          user_id: string
        }
        Insert: {
          created_at?: string
          delay_minutes?: number | null
          id?: string
          medication_id: string
          notes?: string | null
          scheduled_time: string
          status?: string
          taken_time?: string | null
          user_id: string
        }
        Update: {
          created_at?: string
          delay_minutes?: number | null
          id?: string
          medication_id?: string
          notes?: string | null
          scheduled_time?: string
          status?: string
          taken_time?: string | null
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "medication_logs_medication_id_fkey"
            columns: ["medication_id"]
            isOneToOne: false
            referencedRelation: "medications"
            referencedColumns: ["id"]
          },
        ]
      }
      medications: {
        Row: {
          created_at: string
          dosage: string
          end_date: string | null
          frequency: string
          frequency_hours: number
          id: string
          instructions: string | null
          is_critical: boolean
          name: string
          prescriber: string | null
          start_date: string
          updated_at: string
          user_id: string
        }
        Insert: {
          created_at?: string
          dosage: string
          end_date?: string | null
          frequency: string
          frequency_hours: number
          id?: string
          instructions?: string | null
          is_critical?: boolean
          name: string
          prescriber?: string | null
          start_date: string
          updated_at?: string
          user_id: string
        }
        Update: {
          created_at?: string
          dosage?: string
          end_date?: string | null
          frequency?: string
          frequency_hours?: number
          id?: string
          instructions?: string | null
          is_critical?: boolean
          name?: string
          prescriber?: string | null
          start_date?: string
          updated_at?: string
          user_id?: string
        }
        Relationships: []
      }
      profiles: {
        Row: {
          additional_medical_info: string | null
          allergies: string | null
          avatar_url: string | null
          created_at: string
          current_symptoms: string | null
          date_of_birth: string | null
          display_name: string | null
          emergency_contact_name: string | null
          emergency_contact_phone: string | null
          gender: string | null
          id: string
          medical_conditions: string | null
          medical_record_number: string | null
          primary_role: string | null
          updated_at: string
          user_id: string
          user_roles: Json | null
        }
        Insert: {
          additional_medical_info?: string | null
          allergies?: string | null
          avatar_url?: string | null
          created_at?: string
          current_symptoms?: string | null
          date_of_birth?: string | null
          display_name?: string | null
          emergency_contact_name?: string | null
          emergency_contact_phone?: string | null
          gender?: string | null
          id?: string
          medical_conditions?: string | null
          medical_record_number?: string | null
          primary_role?: string | null
          updated_at?: string
          user_id: string
          user_roles?: Json | null
        }
        Update: {
          additional_medical_info?: string | null
          allergies?: string | null
          avatar_url?: string | null
          created_at?: string
          current_symptoms?: string | null
          date_of_birth?: string | null
          display_name?: string | null
          emergency_contact_name?: string | null
          emergency_contact_phone?: string | null
          gender?: string | null
          id?: string
          medical_conditions?: string | null
          medical_record_number?: string | null
          primary_role?: string | null
          updated_at?: string
          user_id?: string
          user_roles?: Json | null
        }
        Relationships: []
      }
      seizures: {
        Row: {
          created_at: string
          duration_seconds: number | null
          environment: string | null
          id: string
          medication_delay_minutes: number | null
          medication_delayed: boolean | null
          notes: string | null
          occurred_at: string
          recovery_time_minutes: number | null
          seizure_type: string
          severity: number
          triggers: string[] | null
          updated_at: string
          user_id: string
        }
        Insert: {
          created_at?: string
          duration_seconds?: number | null
          environment?: string | null
          id?: string
          medication_delay_minutes?: number | null
          medication_delayed?: boolean | null
          notes?: string | null
          occurred_at: string
          recovery_time_minutes?: number | null
          seizure_type: string
          severity: number
          triggers?: string[] | null
          updated_at?: string
          user_id: string
        }
        Update: {
          created_at?: string
          duration_seconds?: number | null
          environment?: string | null
          id?: string
          medication_delay_minutes?: number | null
          medication_delayed?: boolean | null
          notes?: string | null
          occurred_at?: string
          recovery_time_minutes?: number | null
          seizure_type?: string
          severity?: number
          triggers?: string[] | null
          updated_at?: string
          user_id?: string
        }
        Relationships: []
      }
      stories: {
        Row: {
          approved: boolean
          author_email: string | null
          author_initials: string | null
          author_name: string | null
          category_id: number | null
          content: string
          created_at: string
          excerpt: string | null
          featured: boolean
          id: number
          read_count: number
          read_minutes: number | null
          read_time: string | null
          title: string
          updated_at: string
        }
        Insert: {
          approved?: boolean
          author_email?: string | null
          author_initials?: string | null
          author_name?: string | null
          category_id?: number | null
          content: string
          created_at?: string
          excerpt?: string | null
          featured?: boolean
          id?: number
          read_count?: number
          read_minutes?: number | null
          read_time?: string | null
          title: string
          updated_at?: string
        }
        Update: {
          approved?: boolean
          author_email?: string | null
          author_initials?: string | null
          author_name?: string | null
          category_id?: number | null
          content?: string
          created_at?: string
          excerpt?: string | null
          featured?: boolean
          id?: number
          read_count?: number
          read_minutes?: number | null
          read_time?: string | null
          title?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "stories_category_id_fkey"
            columns: ["category_id"]
            isOneToOne: false
            referencedRelation: "categories"
            referencedColumns: ["id"]
          },
        ]
      }
      user_conditions: {
        Row: {
          condition: Database["public"]["Enums"]["medical_condition"]
          created_at: string
          diagnosed_date: string | null
          id: string
          notes: string | null
          severity: string | null
          status: string
          updated_at: string
          user_id: string
        }
        Insert: {
          condition: Database["public"]["Enums"]["medical_condition"]
          created_at?: string
          diagnosed_date?: string | null
          id?: string
          notes?: string | null
          severity?: string | null
          status?: string
          updated_at?: string
          user_id: string
        }
        Update: {
          condition?: Database["public"]["Enums"]["medical_condition"]
          created_at?: string
          diagnosed_date?: string | null
          id?: string
          notes?: string | null
          severity?: string | null
          status?: string
          updated_at?: string
          user_id?: string
        }
        Relationships: []
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      get_user_conditions: {
        Args: Record<PropertyKey, never>
        Returns: Database["public"]["Enums"]["medical_condition"][]
      }
      user_has_condition: {
        Args: {
          condition_name: Database["public"]["Enums"]["medical_condition"]
        }
        Returns: boolean
      }
    }
    Enums: {
      medical_condition:
        | "epilepsy"
        | "diabetes_type_1"
        | "diabetes_type_2"
        | "hypertension"
        | "asthma"
        | "depression"
        | "anxiety"
        | "bipolar_disorder"
        | "migraine"
        | "chronic_pain"
        | "fibromyalgia"
        | "arthritis"
        | "heart_disease"
        | "stroke"
        | "parkinsons"
        | "alzheimers"
        | "multiple_sclerosis"
        | "crohns_disease"
        | "ulcerative_colitis"
        | "lupus"
        | "thyroid_disorder"
        | "kidney_disease"
        | "liver_disease"
        | "cancer"
        | "copd"
        | "sleep_apnea"
        | "eating_disorder"
        | "substance_abuse"
        | "ptsd"
        | "adhd"
        | "autism"
        | "other"
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

// Helper types for easier usage
type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">
type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">]

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] &
        DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] &
        DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R
      }
      ? R
      : never
    : never

export type UserRole = 'patient' | 'clinician' | 'researcher' | 'public';

// New multi-role system - everyone has patient role as base
export interface UserRoles {
  patient: boolean; // Always true for authenticated users
  clinician: boolean;
  researcher: boolean;
}

export interface User {
  id: string;
  email: string;
  roles: UserRoles;
  primaryRole: UserRole; // For UI purposes - which dashboard to show primarily
  profile?: {
    displayName: string;
    avatar?: string;
    organization?: string;
    licenseNumber?: string; // for clinicians
    researchInstitution?: string; // for researchers
  };
}

export interface NavigationItem {
  title: string;
  path: string;
  icon?: string;
  description?: string;
  badge?: string;
  children?: NavigationItem[];
}

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema["Enums"]
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never


// Story type for client-side use
export interface Story {
  id: number;
  title: string;
  excerpt: string | null;
  content: string | null;
  author_name: string | null;
  author_email: string | null;
  author_initials: string | null;
  category_id: number | null;
  featured: boolean;
  read_time: string | null;
  read_minutes: number | null;
  read_count: number | null;
  approved: boolean;
  created_at: string;
  updated_at: string | null;
}

// Category type for client-side use
export interface Category {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
}

// MediaArticle type for client-side use
export interface MediaArticle {
  id: number;
  name: string;
  quotes: string | null;
  link: string | null;
  tagline: string | null;
  featured: boolean;
  created_at: string;
  updated_at: string | null;
}

// Helper function to map Supabase response to Story type
export function mapSupabaseStoryToStory(supabaseStory: any): Story {
  return {
    id: supabaseStory.id,
    title: supabaseStory.title,
    excerpt: supabaseStory.excerpt || null,
    content: supabaseStory.content || null,
    author_name: supabaseStory.author_name || null,
    author_email: supabaseStory.author_email || null,
    author_initials: supabaseStory.author_initials || null,
    category_id: supabaseStory.category_id || null,
    featured: supabaseStory.featured || false,
    read_time: supabaseStory.read_time || null,
    read_minutes: supabaseStory.read_minutes || null,
    read_count: supabaseStory.read_count || 0,
    approved: supabaseStory.approved || false,
    created_at: supabaseStory.created_at,
    updated_at: supabaseStory.updated_at || null,
  };
}

// Helper function to map Supabase response to Category type
export function mapSupabaseCategoryToCategory(supabaseCategory: any): Category {
  return {
    id: supabaseCategory.id,
    name: supabaseCategory.name,
    description: supabaseCategory.description || null,
    created_at: supabaseCategory.created_at,
  };
}

// Helper function to map Supabase response to MediaArticle type
export function mapSupabaseMediaArticleToMediaArticle(supabaseMediaArticle: any): MediaArticle {
  return {
    id: supabaseMediaArticle.id,
    name: supabaseMediaArticle.name,
    quotes: supabaseMediaArticle.quotes || null,
    link: supabaseMediaArticle.link || null,
    tagline: supabaseMediaArticle.tagline || null,
    featured: supabaseMediaArticle.featured || false,
    created_at: supabaseMediaArticle.created_at,
    updated_at: supabaseMediaArticle.updated_at || null,
  };
}   

export const Constants = {
  public: {
    Enums: {
      medical_condition: [
        "epilepsy",
        "diabetes_type_1", 
        "diabetes_type_2",
        "hypertension",
        "asthma",
        "depression",
        "anxiety",
        "bipolar_disorder",
        "migraine",
        "chronic_pain",
        "fibromyalgia",
        "arthritis",
        "heart_disease",
        "stroke",
        "parkinsons",
        "alzheimers",
        "multiple_sclerosis",
        "crohns_disease",
        "ulcerative_colitis",
        "lupus",
        "thyroid_disorder",
        "kidney_disease",
        "liver_disease",
        "cancer",
        "copd",
        "sleep_apnea",
        "eating_disorder",
        "substance_abuse",
        "ptsd",
        "adhd",
        "autism",
        "other",
      ],
    },
  },
} as const;
